import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import communityApi from '../../api/communityApi';
import { ImageInterface } from "../../store/community/imagesSlice";
import imageMakerUrls from "../../configs/community/communityUrls";

const PREFIX = 'community';

const processStrategy = (value: any) => {
  return {
    ...value,
    url: imageMakerUrls.imagePath(value.id),
    file_url: imageMakerUrls.remoteUrl(value.file_url)
  };
}

export const imagesEntity = new schema.Entity(
  'images',
  {},
  {
    processStrategy
  }
);

export const imageEntity = new schema.Entity(
  'image',
  {},
  {
    processStrategy
  }
);

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      image: ImageInterface
    }
  >(response.data.image, imageEntity);
  return normalized.entities;
}

const normalizeUploadInstance = (response: any) => {
  const normalized = normalize<
    any,
    {
      image: ImageInterface
    }
    >(response.image, imageEntity);
  return normalized.entities;
}

const normalizeCollection = (response: AxiosResponse, silentId?: number) => {
  const normalized  = normalize<
    any,
    {
      images: { [key: string]: ImageInterface },
    }
    >(response.data.images, [imagesEntity]);

  return {
    images: normalized.entities?.images || {},
    ids: normalized.result,
    count: response.data.meta?.total_count,
    silentId: silentId
  };
}

export const fetchImages = createAsyncThunk(
  `${PREFIX}_images/fetchImages`,
  async ({ params, silentId }: { params: object; silentId?: number }) => {
    const response = await communityApi.get(`/onesky/images`, { params })
    return normalizeCollection(response, silentId);
  }
);

export const uploadImage = createAsyncThunk(
  `${PREFIX}_images/createImage`,
  async (response: object, { rejectWithValue }) => {
    try {
      return normalizeUploadInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const createImage = createAsyncThunk(
  `${PREFIX}_images/createImage`,
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await communityApi.post(`/onesky/images`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const fetchImage = createAsyncThunk(
  `${PREFIX}_images/fetchImage`,
  async (id: number | string) => {
    const response = await communityApi.get(`/onesky/images/${id}`);
    return normalizeInstance(response);
  }
);

export const updateImage = createAsyncThunk(
  `${PREFIX}_images/updateImage`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await communityApi.patch(`/onesky/images/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyImage = createAsyncThunk(
  `${PREFIX}_images/destroyImage`,
  async (id: number) => {
    await communityApi.delete(`/onesky/images/${id}`);
    return { id: id };
  }
);

export const groupDestroyImage = createAsyncThunk(
  `${PREFIX}_images/groupDestroyImages`,
  async (ids: any, { rejectWithValue }) => {
    try {
      const response = await communityApi.patch(`/onesky/images/group_destroy`, { ids });
      return {
        ids: response.data.ids,
      };
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);
